import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isCognitoConfigured } from '../config/cognito';
import { cognitoAuthService } from '../services/cognitoAuth.service';

/** Detail from spinora-session-invalidated: code = SESSION_INVALIDATED (signed in on another device). */
type SessionInvalidatedDetail = { message?: string; code?: string };

const DEBOUNCE_MS = 8000; // Debounce duplicate events (401 + storage, or multiple in-flight); avoid flicker during retries

/**
 * Full-screen overlay when the session is invalidated (signed in elsewhere, or token expired, etc.).
 * Listens for spinora-session-invalidated; shows appropriate message from server code.
 * Debounces duplicate events (e.g. 401 + storage event, or multiple in-flight requests) so modal appears once.
 */
export const SessionInvalidatedScreen: React.FC = () => {
  const [show, setShow] = useState(false);
  const [detail, setDetail] = useState<SessionInvalidatedDetail>({});
  const [isTryingAgain, setIsTryingAgain] = useState(false);
  const lastShownRef = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (ev: Event) => {
      const now = Date.now();
      if (now - lastShownRef.current < DEBOUNCE_MS) return;
      lastShownRef.current = now;
      const d = (ev as CustomEvent<SessionInvalidatedDetail>).detail ?? {};
      setDetail(d);
      setShow(true);
    };
    window.addEventListener('spinora-session-invalidated', handler);
    return () => window.removeEventListener('spinora-session-invalidated', handler);
  }, []);

  const handleSignInAgain = () => {
    setShow(false);
    navigate('/', { replace: true });
  };

  const handleTryAgain = async () => {
    if (!isCognitoConfigured()) return;
    setIsTryingAgain(true);
    try {
      const session = await cognitoAuthService.getCurrentSession();
      const token = session.getIdToken().getJwtToken();
      if (token && typeof localStorage !== 'undefined') {
        localStorage.setItem('authToken', token);
        setShow(false);
        window.location.reload();
      }
    } catch {
      /* session truly invalid; keep overlay */
    } finally {
      setIsTryingAgain(false);
    }
  };

  const isAnotherDevice = detail?.code === 'SESSION_INVALIDATED';
  const isAccountDeleted = detail?.code === 'ACCOUNT_DELETED';
  const canTryAgain = !isAnotherDevice && !isAccountDeleted && isCognitoConfigured();
  const title = isAccountDeleted
    ? 'Account has been removed'
    : isAnotherDevice
      ? 'Signed in on another device'
      : 'Your session has ended';
  const subtitle = isAccountDeleted
    ? 'This account was permanently deleted. Sign up again to create a new account.'
    : isAnotherDevice
      ? 'Only one session is allowed per account. Return to the home screen to sign in again.'
      : 'Return to the home screen to sign in again.';

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[99999] flex flex-col items-center justify-center p-6 bg-gradient-to-br from-gray-950 via-gray-900 to-black text-white"
      role="alert"
      aria-live="polite"
    >
      <div className="flex flex-col items-center justify-center max-w-md text-center space-y-8">
        <img
          src="/SpinorLogo.png"
          alt="Spinora"
          className="h-14 md:h-16 w-auto brightness-0 invert"
        />
        <div className="space-y-3">
          <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-yellow-200 via-yellow-100 to-white bg-clip-text text-transparent">
            {title}
          </h1>
          <p className="text-gray-300 text-sm md:text-base">
            {subtitle}
          </p>
        </div>
        <div className="flex flex-col gap-3 w-full max-w-xs">
          {canTryAgain && (
            <button
              type="button"
              onClick={handleTryAgain}
              disabled={isTryingAgain}
              className="w-full px-6 py-3 text-sm font-semibold bg-white/10 hover:bg-white/15 border border-white/20 text-white rounded-xl transition-all disabled:opacity-50"
            >
              {isTryingAgain ? 'Checking…' : 'Try again'}
            </button>
          )}
          <button
            type="button"
            onClick={handleSignInAgain}
            className="w-full px-6 py-3 text-sm font-semibold bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 hover:from-yellow-400 hover:via-yellow-500 hover:to-yellow-600 text-white rounded-xl shadow-lg shadow-yellow-600/25 border border-yellow-600/30 transition-all hover:scale-[1.02]"
          >
            Return to home screen
          </button>
        </div>
      </div>
    </div>
  );
};
